<template>
	<section>
		<loader :dialog="loading"></loader>
		<floating-alert
			:message="alertMessage"
			:show="alertShow"
			:type="alertType"
		></floating-alert>

		<v-card elevation="2" rounded="xl">
			<!-- Header -->
			<div class="sl-header">
				<div class="sl-header-left">
					<div class="sl-icon-box">
						<v-icon size="18" color="white">mdi-yoga</v-icon>
					</div>
					<div>
						<p class="sl-title">Liste des séances</p>
						<p class="sl-subtitle">Gestion des séances de sport</p>
					</div>
				</div>
				<v-text-field
					v-model="search"
					prepend-inner-icon="mdi-magnify"
					placeholder="Rechercher..."
					outlined
					dense
					hide-details
					class="sl-search"
					color="blue darken-2"
				></v-text-field>

				<!-- Create/Edit dialog -->
				<v-dialog v-model="dialog" max-width="560px" scrollable>
					<template v-slot:activator="{ on, attrs }">
						<v-btn
							depressed
							rounded
							class="sl-add-btn ml-3"
							v-bind="attrs"
							v-on="on"
						>
							<v-icon left small>mdi-plus</v-icon>
							Nouveau
						</v-btn>
					</template>
					<v-card rounded="xl" class="pa-2">
						<div class="sl-dialog-header">
							<div class="sl-icon-box-sm">
								<v-icon size="16" color="white">mdi-yoga</v-icon>
							</div>
							<div>
								<p class="sl-dialog-title">{{ formTitle }}</p>
								<p class="sl-dialog-sub">Séance de sport</p>
							</div>
							<v-spacer></v-spacer>
							<v-btn icon small @click="close">
								<v-icon color="#6b7280">mdi-close</v-icon>
							</v-btn>
						</div>
						<v-divider></v-divider>
						<v-card-text class="pt-5">
							<v-row>
								<v-col cols="12">
									<v-select
										:items="coaches"
										item-text="fullName"
										item-value="_id"
										v-model="editedItem.coachId"
										label="Coach"
										outlined
										dense
										rounded
										prepend-inner-icon="mdi-whistle"
										hide-details
										class="mb-3"
									></v-select>
								</v-col>
								<v-col cols="12">
									<v-text-field
										v-model="editedItem.label"
										label="Intitulé de la séance"
										outlined
										dense
										rounded
										prepend-inner-icon="mdi-text"
										hide-details
										class="mb-3"
									></v-text-field>
								</v-col>
								<v-col cols="12">
									<label class="field-label">Description</label>
									<VueEditor
										v-model="editedItem.description"
										:editorToolbar="customToolbar"
										class="mt-2"
									></VueEditor>
								</v-col>
							</v-row>
						</v-card-text>
						<v-card-actions class="pa-4 pt-2">
							<v-btn text color="error" rounded @click="close">Annuler</v-btn>
							<v-spacer></v-spacer>
							<v-btn depressed rounded class="sl-save-btn" @click="save">
								Enregistrer
							</v-btn>
						</v-card-actions>
					</v-card>
				</v-dialog>
			</div>

			<v-divider></v-divider>

			<!-- Table -->
			<v-data-table
				:loading="loadingTable"
				loading-text="Chargement en cours..."
				:headers="headers"
				:items="sessions"
				:search="search"
				class="sl-table"
				:footer-props="{
					'items-per-page-text': 'Lignes par page',
					'items-per-page-all-text': 'Tout',
				}"
			>
				<template v-slot:[`item.description`]="{ item }">
					<span class="sl-desc" v-html="item.description"></span>
				</template>

				<template v-slot:[`item.actions`]="{ item }">
					<v-tooltip bottom>
						<template v-slot:activator="{ on, attrs }">
							<v-btn icon small v-bind="attrs" v-on="on" @click="sessionAttribution(item)">
								<v-icon size="17" color="#1565c0">mdi-account-plus</v-icon>
							</v-btn>
						</template>
						<span>Attribuer</span>
					</v-tooltip>
					<v-btn icon small @click="editItem(item)">
						<v-icon size="17" color="#374151">mdi-pencil</v-icon>
					</v-btn>
					<v-btn icon small @click="deleteItem(item)">
						<v-icon size="17" color="#c62828">mdi-delete</v-icon>
					</v-btn>
				</template>

				<template v-slot:[`footer.page-text`]="items">
					{{ items.pageStart }}-{{ items.pageStop }} sur {{ items.itemsLength }}
				</template>
				<template v-slot:no-data>
					<v-btn text color="primary" @click="initialize">Actualiser</v-btn>
				</template>
			</v-data-table>

			<!-- Total des prix -->
			<div class="sl-total-bar">
				<span class="sl-total-label">Total des séances</span>
				<span class="sl-total-value">{{ totalPrice.toLocaleString('fr') }} FCFA</span>
			</div>
		</v-card>

		<!-- Attribution dialog -->
		<v-dialog v-model="attributionDialog" max-width="440px">
			<v-card rounded="xl" class="pa-2">
				<div class="attr-header">
					<div class="attr-icon-box">
						<v-icon size="16" color="white">mdi-account-clock</v-icon>
					</div>
					<div>
						<p class="attr-title">Attribution de la séance</p>
						<p class="attr-sub">{{ session.label }}</p>
					</div>
					<v-spacer></v-spacer>
					<v-btn icon small @click="attributionDialog = false">
						<v-icon color="#6b7280">mdi-close</v-icon>
					</v-btn>
				</div>
				<v-divider></v-divider>
				<v-card-text class="pt-4">
					<v-select
						:items="clients"
						item-text="fullName"
						item-value="_id"
						v-model="attribution.customerId"
						label="Client"
						outlined
						dense
						rounded
						prepend-inner-icon="mdi-account"
						hide-details
						class="mb-4"
					></v-select>
					<label class="field-label">Quantité</label>
					<vue-number-input
						v-model="attribution.sessionQuantity"
						center
						size="small"
						controls
						class="mt-2"
					></vue-number-input>
				</v-card-text>
				<v-card-actions class="pa-4 pt-2">
					<v-btn text color="error" rounded @click="attributionDialog = false">Annuler</v-btn>
					<v-spacer></v-spacer>
					<v-btn depressed rounded class="attr-confirm-btn" @click="saveProgrammedSession">
						<v-icon left small>mdi-check</v-icon>
						Confirmer
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Delete dialog -->
		<v-dialog v-model="dialogDelete" max-width="440px">
			<v-card rounded="xl" class="pa-2">
				<div class="del-header">
					<div class="del-icon">
						<v-icon size="18" color="white">mdi-alert</v-icon>
					</div>
					<p class="del-title">Supprimer cette séance ?</p>
				</div>
				<v-card-actions class="pa-4 pt-2">
					<v-btn text color="grey" rounded @click="closeDelete">Annuler</v-btn>
					<v-spacer></v-spacer>
					<v-btn depressed rounded class="del-confirm-btn" @click="deleteItemConfirm">
						Supprimer
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</section>
</template>

<script>
import service from "@/services";
import Loader from "@/components/Loader.vue";
import FloatingAlert from "@/components/FloatingAlert.vue";
export default {
	components: { Loader, FloatingAlert },
	data() {
		return {
			search: "",
			loadingTable: false,
			dialog: false,
			dialogDelete: false,
			attributionDialog: false,
			loading: false,
			alertMessage: "",
			alertShow: false,
			alertType: "",
			headers: [
				{ text: "Intitulé", align: "start", value: "label" },
				{ text: "Description", value: "description" },
				{ text: "Coach associé", value: "user.fullName" },
				{ text: "Date de création", value: "createdAt" },
				{ text: "Actions", value: "actions", sortable: false },
			],
			sessions: [],
			session: {},
			editedIndex: -1,
			editedItem: { label: "", description: "", coachId: "" },
			attribution: { sessionId: "", customerId: "", sessionQuantity: 1 },
			defaultItem: { label: "", description: "", coachId: "" },
			clients: [],
			coaches: [],
			customToolbar: [
				[{ header: [false, 1, 2, 3, 4, 5, 6] }],
				["bold", "italic", "underline"],
				[{ color: [] }],
				[{ list: "ordered" }, { list: "bullet" }],
			],
		};
	},
	computed: {
		formTitle() {
			return this.editedIndex === -1 ? "Nouvelle séance" : "Modifier une séance";
		},
		totalPrice() {
			return this.sessions.reduce((sum, s) => sum + (parseFloat(s.label) || 0), 0);
		},
	},
	watch: {
		dialog(val) { val || this.close(); },
		dialogDelete(val) { val || this.closeDelete(); },
	},
	created() {
		this.initialize();
		this.getAllCoaches();
		this.getClients();
	},
	methods: {
		initialize() {
			this.sessions = [];
			this.loadingTable = true;
			service.getSessions().then((r) => { this.sessions = r.data?.sessions || []; this.loadingTable = false; }).catch((e) => { console.log(e); this.loadingTable = false; });
		},
		getAllCoaches() {
			service.getSpecificUsers("COACH").then((r) => { this.coaches = r.data?.users || r.data || []; }).catch((e) => console.log(e));
		},
		getClients() {
			service.getClients().then((r) => { this.clients = r.data?.customers || r.data || []; }).catch((e) => console.log(e));
		},
		sessionAttribution(session) {
			this.session = session;
			this.attribution.sessionId = session._id;
			this.attributionDialog = true;
		},
		saveProgrammedSession() {
			this.alertShow = false;
			this.loading = true;
			service.attributeSession(this.attribution).then((r) => {
				this.alertMessage = r.message;
				this.alertShow = true;
				this.alertType = "green";
			}).catch((e) => {
				console.log(e);
				this.alertMessage = e;
				this.alertShow = true;
				this.alertType = "red";
			}).finally(() => {
				this.loading = false;
				this.attributionDialog = false;
			});
		},
		editItem(item) { this.editedIndex = this.sessions.indexOf(item); this.editedItem = Object.assign({}, item); this.dialog = true; },
		deleteItem(item) { this.editedIndex = this.sessions.indexOf(item); this.editedItem = Object.assign({}, item); this.dialogDelete = true; },
		deleteItemConfirm() {
			this.alertShow = false;
			this.loading = true;
			service.deleteSession(this.editedItem._id).then((r) => {
				this.sessions.splice(this.editedIndex, 1);
				this.closeDelete();
				this.loading = false;
				this.alertMessage = r.message;
				this.alertShow = true;
				this.alertType = "green";
			}).catch((e) => {
				this.loading = false;
				this.alertMessage = e.message;
				this.alertShow = true;
				this.alertType = "red";
			});
		},
		close() { this.dialog = false; this.$nextTick(() => { this.editedItem = Object.assign({}, this.defaultItem); this.editedIndex = -1; }); },
		closeDelete() { this.dialogDelete = false; this.$nextTick(() => { this.editedItem = Object.assign({}, this.defaultItem); this.editedIndex = -1; }); },
		save() {
			if (this.editedIndex > -1) {
				this.loading = true;
				service.updateSession(this.editedItem._id, this.editedItem).then(() => { Object.assign(this.sessions[this.editedIndex], this.editedItem); this.loading = false; this.close(); this.initialize(); }).catch((e) => { console.log(e); this.loading = false; });
			} else {
				this.loading = true;
				service.addSession(this.editedItem).then(() => { this.close(); this.initialize(); this.loading = false; }).catch((e) => { console.log(e); this.loading = false; });
			}
		},
	},
};
</script>

<style scoped>
.sl-header {
	display: flex;
	align-items: center;
	padding: 18px 20px 14px;
	gap: 14px;
	flex-wrap: wrap;
}
.sl-header-left {
	display: flex;
	align-items: center;
	gap: 14px;
	flex: 1;
	min-width: 200px;
}
.sl-icon-box {
	width: 38px;
	height: 38px;
	border-radius: 10px;
	background: #1565c0;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
}
.sl-title {
	font-size: 0.92rem;
	font-weight: 700;
	color: #111827;
	margin: 0 0 2px;
	font-family: Montserrat, sans-serif;
}
.sl-subtitle {
	font-size: 0.72rem;
	color: #9ca3af;
	margin: 0;
	font-family: Montserrat, sans-serif;
}
.sl-search {
	max-width: 260px;
}
.sl-add-btn {
	background: #c62828 !important;
	color: white !important;
	font-family: Montserrat, sans-serif;
	font-weight: 700;
	font-size: 0.82rem;
}
.sl-table {
	font-family: Montserrat, sans-serif;
}
.sl-total-bar {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 14px 20px;
	border-top: 1px solid #f3f4f6;
	background: #f9fafb;
	border-radius: 0 0 12px 12px;
	font-family: Montserrat, sans-serif;
}
.sl-total-label {
	font-size: 0.82rem;
	font-weight: 600;
	color: #6b7280;
}
.sl-total-value {
	font-size: 0.92rem;
	font-weight: 800;
	color: #111827;
}
.sl-desc {
	font-size: 0.78rem;
	color: #6b7280;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
}
.sl-dialog-header {
	display: flex;
	align-items: center;
	gap: 14px;
	padding: 18px 20px 14px;
}
.sl-icon-box-sm {
	width: 34px;
	height: 34px;
	border-radius: 8px;
	background: #1565c0;
	display: flex;
	align-items: center;
	justify-content: center;
}
.sl-dialog-title {
	font-size: 0.88rem;
	font-weight: 700;
	color: #111827;
	margin: 0 0 2px;
	font-family: Montserrat, sans-serif;
}
.sl-dialog-sub {
	font-size: 0.68rem;
	color: #9ca3af;
	margin: 0;
	font-family: Montserrat, sans-serif;
}
.field-label {
	font-size: 0.75rem;
	font-weight: 600;
	color: #6b7280;
	font-family: Montserrat, sans-serif;
}
.sl-save-btn {
	background: #1565c0 !important;
	color: white !important;
	font-family: Montserrat, sans-serif;
	font-weight: 700;
	font-size: 0.82rem;
}
.attr-header {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 16px 20px 12px;
}
.attr-icon-box {
	width: 34px;
	height: 34px;
	border-radius: 8px;
	background: #1565c0;
	display: flex;
	align-items: center;
	justify-content: center;
}
.attr-title {
	font-size: 0.88rem;
	font-weight: 700;
	color: #111827;
	margin: 0 0 2px;
	font-family: Montserrat, sans-serif;
}
.attr-sub {
	font-size: 0.72rem;
	color: #9ca3af;
	margin: 0;
	font-family: Montserrat, sans-serif;
}
.attr-confirm-btn {
	background: #c62828 !important;
	color: white !important;
	font-family: Montserrat, sans-serif;
	font-weight: 700;
	font-size: 0.82rem;
}
.del-header {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 16px 20px 12px;
}
.del-icon {
	width: 34px;
	height: 34px;
	border-radius: 8px;
	background: #c62828;
	display: flex;
	align-items: center;
	justify-content: center;
}
.del-title {
	font-size: 0.9rem;
	font-weight: 700;
	color: #111827;
	margin: 0;
	font-family: Montserrat, sans-serif;
}
.del-confirm-btn {
	background: #c62828 !important;
	color: white !important;
	font-family: Montserrat, sans-serif;
	font-weight: 700;
	font-size: 0.82rem;
}
</style>

